import React from "react";
import { Link } from "react-router-dom";
import { Star, Trophy } from "lucide-react";
import { buildOffers, effectivePrice, imgFallback, inr, mvPrice, siteColor } from "@/lib/compare";

const MiniCompare = ({ product }) => {
  const offers = buildOffers(product);
  if (offers.length < 2) {
    return <p className="text-xs text-primary-400 italic" data-testid={`card-compare-empty-${product.id}`}>Only on M Ventures right now</p>;
  }
  const best = Math.min(...offers.map(effectivePrice));
  return (
    <ul className="space-y-1.5" data-testid={`card-compare-${product.id}`}>
      {offers.slice(0, 4).map((o) => (
        <li key={o.id} className={`flex items-center justify-between text-xs rounded-lg px-2 py-1.5 ${effectivePrice(o) === best ? "bg-secondary-100 font-bold text-primary-900" : "bg-primary-50 text-primary-700"}`}>
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: o.isMV ? "#7C3AED" : siteColor(o.site) }} />
            <span className="truncate">{o.isMV ? <><span className="sm:hidden">Us</span><span className="hidden sm:inline">M Ventures</span></> : o.site}</span>
            {effectivePrice(o) === best && <Trophy className="w-3 h-3 text-secondary-700 shrink-0" />}
          </span>
          <span>{inr(effectivePrice(o))}</span>
        </li>
      ))}
    </ul>
  );
};

export const ProductCard = ({ product, compare = false, testIdPrefix = "product-card" }) => {
  const price = mvPrice(product);
  const off = product.discounted_price ? Math.round(((product.original_price - product.discounted_price) / product.original_price) * 100) : 0;
  const offersCount = buildOffers(product).length - 1;
  return (
    <Link
      to={`/product/${product.id}`}
      className="group flex flex-col bg-white rounded-3xl border-2 border-primary-100 overflow-hidden hover:border-primary-300 hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(124,58,237,0.15)] transition-[transform,box-shadow,border-color] duration-200"
      data-testid={`${testIdPrefix}-${product.id}`}
    >
      <div className="relative aspect-square bg-primary-50 overflow-hidden">
        <img src={product.image_url} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" onError={imgFallback} />
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-secondary-500 text-primary-900 text-[11px] font-extrabold shadow">{product.cashback_percent}% CASHBACK</span>
        {off > 0 && <span className="absolute top-3 right-3 px-2 py-1 rounded-full bg-pink-500 text-white text-[11px] font-bold">-{off}%</span>}
        {offersCount > 0 && !compare && (
          <span className="absolute bottom-3 left-3 px-2 py-1 rounded-full bg-white/90 text-primary-700 text-[11px] font-bold">Compare {offersCount + 1} stores</span>
        )}
      </div>
      <div className="p-3 sm:p-4 flex flex-col gap-2 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-wider text-primary-400">{product.brand_name}</p>
        <h3 className="text-sm sm:text-base font-semibold text-primary-900 line-clamp-2 leading-snug">{product.title}</h3>
        {product.rating ? (
          <span className="inline-flex items-center gap-1 text-xs text-primary-600"><Star className="w-3.5 h-3.5 fill-secondary-500 text-secondary-500" />{product.rating}{product.review_count ? ` (${product.review_count.toLocaleString("en-IN")})` : ""}</span>
        ) : null}
        <div className="flex items-baseline gap-2 mt-auto">
          <span className="text-lg font-extrabold text-primary-900">{inr(price)}</span>
          {product.discounted_price && <span className="text-xs text-primary-300 line-through">{inr(product.original_price)}</span>}
        </div>
        {compare && <MiniCompare product={product} />}
      </div>
    </Link>
  );
};
